import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Avatar,
  Checkbox,
  Menu,
  ListItemIcon,
  ListItemText,
  OutlinedInput,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Refresh,
  Search,
  FilterList,
  ExpandLess,
  ExpandMore,
  Notifications,
  MoreVert,
  Clear,
  CheckCircle,
  AccessTime,
} from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { delay } from '../../utils/requestThrottle';
import * as yup from 'yup';
import userService from '../../services/userService';
import clusterService from '../../services/clusterService';
import storeService from '../../services/storeService';
import shiftService from '../../services/shiftService';
import vendorService from '../../services/vendorService';
import { USER_ROLES, USER_ROLE_OPTIONS, CATEGORY_LEVELS, CATEGORY_LEVEL_OPTIONS, EMPLOYEE_TYPE_OPTIONS, GENDER_OPTIONS } from '../../constants';
import { User, Cluster, Store, UserWorkScheduleEntry } from '../../types';
import Layout from '../../components/layout/Layout';
import { useNavigate } from 'react-router-dom';
import ProfileAvatar from '../../components/common/ProfileAvatar';
import { useAuth } from '../../hooks/useAuth';

// Validation schema - dynamically validates based on role
const createUserSchema = (role?: string) => yup.object({
  first_name: yup.string().required('First name is required'),
  last_name: yup.string().required('Last name is required'),
  phone: yup.string()
    .required('Phone number is required')
    .matches(/^[6-9]\d{9}$/, 'Phone must be 10 digits starting with 6, 7, 8, or 9'),
  email: yup.string().email('Invalid email format').optional(),
  official_email: yup.string().email('Invalid email format').optional(),
  role: yup.string().oneOf(USER_ROLE_OPTIONS.map(opt => opt.value), 'Invalid role').required('Role is required'),
  cluster_id: role === USER_ROLES.super_admin 
    ? yup.string().optional() 
    : yup.string().required('Cluster is required'),
  store_id: role === USER_ROLES.super_admin 
    ? yup.string().optional() 
    : yup.string().required('Store is required'),
  shift_id: role === USER_ROLES.super_admin 
    ? yup.string().optional() 
    : yup.string().required('Shift is required'),
  vendor_id: yup.string().optional(),
  job_title: yup.string().optional(),
  employee_type: yup.string().oneOf(['permanent', 'contract', 'temporary', 'intern', '']).optional(),
  category_level: yup.string().oneOf(Object.values(CATEGORY_LEVELS)).optional(),
  joining_date: yup.date().optional(),
  date_of_birth: yup.date().optional(),
  gender: yup.string().oneOf(['male', 'female', 'other', '']).optional(),
  address: yup.string().optional(),
});

const UsersPage: React.FC = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [clusters, setClusters] = useState<Cluster[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [shifts, setShifts] = useState<any[]>([]);
  const [vendors, setVendors] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Check if current user can edit/regenerate OTP (only CLM and Super Admin, not Store Manager)
  const canEditUser = currentUser?.role === 'super_admin' || currentUser?.role === 'cluster_level_manager';
  const [generatedPIN, setGeneratedPIN] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [openScheduleDialog, setOpenScheduleDialog] = useState(false);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [userSchedule, setUserSchedule] = useState<UserWorkScheduleEntry[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [bulkActionAnchor, setBulkActionAnchor] = useState<null | HTMLElement>(null);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 50 });

  // Filters state
  const [filters, setFilters] = useState({
    search: '',
    role: 'all',
    cluster_id: 'all',
    store_id: 'all',
    vendor_id: 'all',
    category_level: 'all',
    employee_type: 'all',
    is_active: 'true', // Default to showing only active users
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm({
    resolver: yupResolver(createUserSchema(editingUser?.role)),
  });

  const watchedRole = watch('role');
  const isSuperAdmin = watchedRole === USER_ROLES.super_admin || editingUser?.role === USER_ROLES.super_admin;

  // Fetch data
  const fetchUsers = async () => {
    try {
      setLoading(true);
      // Only fetch active users by default (unless filter is set to 'all' or 'false')
      const params: any = { limit: 1000 };
      if (filters.is_active === 'all') {
        // Fetch all users if filter is 'all'
      } else if (filters.is_active === 'false') {
        // Fetch only inactive users
        params.is_active = false;
      } else {
        // Default: fetch only active users
        params.is_active = true;
      }
      const response = await userService.getUsers(params);
      setUsers(response.users);
      setPagination(response.pagination);
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const fetchClusters = async () => {
    try {
      const response = await clusterService.getClusters({ limit: 1000 });
      setClusters(response.clusters);
    } catch (error) {
      console.error('Error fetching clusters:', error);
    }
  };

  const fetchShifts = async () => {
    try {
      const response = await shiftService.getShifts({ limit: 100 });
      setShifts(response.shifts);
    } catch (error) {
      console.error('Error fetching shifts:', error);
    }
  };

  const fetchVendors = async () => {
    try {
      // Only active vendors are offered when assigning staff; deactivated ones
      // stay attached to existing employees but must not be pickable.
      const response = await vendorService.getVendors({ limit: 200, is_active: true });
      setVendors(response.vendors);
    } catch (error) {
      console.error('Error fetching vendors:', error);
    }
  };

  const fetchStores = async () => {
    try {
      const response = await storeService.getStores({ limit: 1000 });
      setStores(response.stores);
    } catch (error) {
      console.error('Error fetching stores:', error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      // Stagger API calls to prevent rate limiting
      // Load critical data first, then optional data
      await fetchUsers();
      await delay(200);
      await fetchClusters();
      await delay(200);
      await fetchStores();
      await delay(200);
      await fetchShifts();
      await delay(200);
      await fetchVendors();
    };
    loadData();
  }, []);

  // Refetch users when is_active filter changes
  useEffect(() => {
    fetchUsers();
  }, [filters.is_active]);

  // Apply filters
  useEffect(() => {
    let filtered = [...users];

    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(user =>
        user.first_name.toLowerCase().includes(searchLower) ||
        user.last_name.toLowerCase().includes(searchLower) ||
        user.employee_id.toLowerCase().includes(searchLower) ||
        user.phone.includes(searchLower) ||
        (user.email && user.email.toLowerCase().includes(searchLower))
      );
    }

    // Role filter
    if (filters.role !== 'all') {
      filtered = filtered.filter(user => user.role === filters.role);
    }

    // Cluster filter
    if (filters.cluster_id !== 'all') {
      filtered = filtered.filter(user => user.cluster_id === filters.cluster_id);
    }

    // Store filter
    if (filters.store_id !== 'all') {
      filtered = filtered.filter(user => user.store_id === filters.store_id);
    }

    // Vendor filter. 'none' surfaces staff with no vendor recorded, which is
    // how you find the gaps in the vendor data.
    if (filters.vendor_id !== 'all') {
      filtered = filters.vendor_id === 'none'
        ? filtered.filter(user => !user.vendor_id)
        : filtered.filter(user => user.vendor_id === filters.vendor_id);
    }

    // Category filter
    if (filters.category_level !== 'all') {
      filtered = filtered.filter(user => user.category_level === filters.category_level);
    }

    // Employee type filter
    if (filters.employee_type !== 'all') {
      filtered = filtered.filter(user => user.employee_type === filters.employee_type);
    }

    // Status filter
    if (filters.is_active !== 'all') {
      const isActive = filters.is_active === 'true';
      filtered = filtered.filter(user => user.is_active === isActive);
    }

    setFilteredUsers(filtered);
  }, [users, filters]);

  const clearFilters = () => {
    setFilters({
      search: '',
      role: 'all',
      cluster_id: 'all',
      store_id: 'all',
      vendor_id: 'all',
      category_level: 'all',
      employee_type: 'all',
      is_active: 'true', // Default to showing only active users
    });
  };

  const DAY_LABELS: { label: string; short: string; value: number }[] = [
    { label: 'Monday', short: 'Mon', value: 1 },
    { label: 'Tuesday', short: 'Tue', value: 2 },
    { label: 'Wednesday', short: 'Wed', value: 3 },
    { label: 'Thursday', short: 'Thu', value: 4 },
    { label: 'Friday', short: 'Fri', value: 5 },
    { label: 'Saturday', short: 'Sat', value: 6 },
    { label: 'Sunday', short: 'Sun', value: 7 },
  ];

  const openWorkSchedule = async (user: User) => {
    try {
      setEditingUser(user);
      setScheduleLoading(true);
      setOpenScheduleDialog(true);

      const result = await userService.getUserWorkSchedule(user.id);
      const scheduleMap = new Map<number, UserWorkScheduleEntry>();
      result.schedule.forEach((entry) => {
        scheduleMap.set(entry.day_of_week, entry);
      });

      const fullSchedule: UserWorkScheduleEntry[] = DAY_LABELS.map((d) => {
        const existing = scheduleMap.get(d.value);
        return (
          existing || {
            user_id: user.id,
            day_of_week: d.value,
            is_week_off: d.value === 7, // default Sunday off
            shift_id: user.shift_id || null,
            shift: null,
          }
        );
      });

      setUserSchedule(fullSchedule);
    } catch (err: any) {
      console.error('Error loading work schedule', err);
      setError(err?.message || 'Failed to load work schedule');
      setOpenScheduleDialog(false);
    } finally {
      setScheduleLoading(false);
    }
  };

  const updateScheduleEntry = (dayIndex: number, patch: Partial<UserWorkScheduleEntry>) => {
    setUserSchedule((prev) =>
      prev.map((entry, idx) => (idx === dayIndex ? { ...entry, ...patch } : entry))
    );
  };

  const handleSaveWorkSchedule = async () => {
    if (!editingUser) return;
    try {
      setScheduleLoading(true);
      const payload = userSchedule.map((entry) => ({
        day_of_week: entry.day_of_week,
        is_week_off: entry.is_week_off,
        shift_id: entry.is_week_off ? null : entry.shift_id || null,
      }));

      await userService.updateUserWorkSchedule(editingUser.id, payload);
      setOpenScheduleDialog(false);
    } catch (err: any) {
      console.error('Error saving work schedule', err);
      setError(err?.message || 'Failed to save work schedule');
    } finally {
      setScheduleLoading(false);
    }
  };

  const handleCreateUser = async (data: any) => {
    try {
      // Clean phone number - remove spaces, dashes, etc. and ensure it's exactly 10 digits
      const cleanPhone = data.phone ? data.phone.replace(/\D/g, '') : '';
      
      // Format dates to ISO string format (YYYY-MM-DD)
      const formattedData: any = {
        first_name: data.first_name,
        last_name: data.last_name,
        phone: cleanPhone,
        role: data.role,
        joining_date: data.joining_date ? new Date(data.joining_date).toISOString().split('T')[0] : undefined,
        job_title: data.job_title || undefined,
        employee_type: data.employee_type || undefined,
        category_level: data.category_level || undefined,
      };
      
      // Only include cluster/store/shift if not super_admin
      if (data.role !== USER_ROLES.super_admin) {
        formattedData.cluster_id = data.cluster_id || undefined;
        formattedData.store_id = data.store_id || undefined;
        formattedData.shift_id = data.shift_id || undefined;
      }

      // Vendor is role-independent and optional. Send null rather than omitting
      // it so clearing the field on edit actually removes the link.
      formattedData.vendor_id = data.vendor_id || null;
      
      // Only include email if provided and not empty
      if (data.email && data.email.trim()) {
        formattedData.email = data.email.trim();
      }
      
      // Only include official_email if provided and not empty
      if (data.official_email && data.official_email.trim()) {
        formattedData.official_email = data.official_email.trim();
      }
      
      // Only include date_of_birth if provided
      if (data.date_of_birth) {
        formattedData.date_of_birth = new Date(data.date_of_birth).toISOString().split('T')[0];
      }
      
      // Only include other optional fields if provided (not empty string)
      if (data.employee_type && data.employee_type.trim()) {
        formattedData.employee_type = data.employee_type;
      }
      if (data.gender && data.gender.trim()) {
        formattedData.gender = data.gender.toLowerCase(); // Ensure lowercase
      }
      if (data.address && data.address.trim()) {
        formattedData.address = data.address;
      }
      if (data.city && data.city.trim()) {
        formattedData.city = data.city;
      }
      if (data.state && data.state.trim()) {
        formattedData.state = data.state;
      }
      if (data.pincode && data.pincode.trim()) {
        formattedData.pincode = data.pincode;
      }
      
      console.log('Creating user with formatted data:', formattedData);
      await userService.createUser(formattedData);
      setOpenDialog(false);
      reset();
      fetchUsers();
    } catch (error: any) {
      console.error('Error creating user:', error);
      console.error('Error response:', error.response?.data);
      
      // Extract validation errors
      let errorMessage = 'Failed to create user';
      
      if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        // Show all validation errors
        const errorMessages = error.response.data.errors.map((err: any) => 
          `${err.field}: ${err.message}`
        );
        errorMessage = `Validation errors:\n${errorMessages.join('\n')}`;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      setError(errorMessage);
    }
  };

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    setValue('first_name', user.first_name);
    setValue('last_name', user.last_name);
    setValue('phone', user.phone);
    setValue('email', user.email || '');
    setValue('official_email', user.official_email || '');
    setValue('role', user.role);
    // Only set cluster/store/shift if not super_admin
    if (user.role !== USER_ROLES.super_admin) {
    setValue('cluster_id', user.cluster_id || '');
    setValue('store_id', user.store_id || '');
    setValue('shift_id', user.shift_id || '');
    setValue('vendor_id', user.vendor_id || '');
    } else {
      setValue('cluster_id', '');
      setValue('store_id', '');
      setValue('shift_id', '');
    }
    setValue('job_title', user.job_title || '');
    setValue('employee_type', (user.employee_type as any) || '');
    setValue('category_level', user.category_level || '');
    if (user.joining_date) {
      setValue('joining_date', new Date(user.joining_date));
    }
    if (user.date_of_birth) {
      setValue('date_of_birth', new Date(user.date_of_birth));
    }
    setValue('gender', (user.gender as any) || '');
    setValue('address', user.address || '');
    setOpenDialog(true);
  };

  const handleUpdateUser = async (data: any) => {
    if (!editingUser) return;
    try {
      // Clean phone number if provided
      const cleanPhone = data.phone ? data.phone.replace(/\D/g, '') : data.phone;
      
      // Format dates to ISO string format (YYYY-MM-DD)
      const formattedData: any = {
        first_name: data.first_name,
        last_name: data.last_name,
        phone: cleanPhone,
        role: data.role,
        joining_date: data.joining_date ? new Date(data.joining_date).toISOString().split('T')[0] : undefined,
        job_title: data.job_title || undefined,
        category_level: data.category_level || undefined,
      };
      
      // Only include cluster/store/shift if not super_admin
      if (data.role !== USER_ROLES.super_admin) {
        formattedData.cluster_id = data.cluster_id || undefined;
        formattedData.store_id = data.store_id || undefined;
        formattedData.shift_id = data.shift_id || undefined;
      }

      // Vendor is role-independent and optional. Send null rather than omitting
      // it so clearing the field on edit actually removes the link.
      formattedData.vendor_id = data.vendor_id || null;
      
      // Only include email if provided and not empty
      if (data.email && data.email.trim()) {
        formattedData.email = data.email.trim();
      }
      
      // Only include official_email if provided and not empty
      if (data.official_email && data.official_email.trim()) {
        formattedData.official_email = data.official_email.trim();
      }
      
      // Only include date_of_birth if provided
      if (data.date_of_birth) {
        formattedData.date_of_birth = new Date(data.date_of_birth).toISOString().split('T')[0];
      }
      
      // Only include other optional fields if provided (not empty string)
      if (data.employee_type && data.employee_type.trim()) {
        formattedData.employee_type = data.employee_type;
      }
      if (data.gender && data.gender.trim()) {
        formattedData.gender = data.gender.toLowerCase(); // Ensure lowercase
      }
      if (data.address && data.address.trim()) {
        formattedData.address = data.address;
      }
      if (data.city && data.city.trim()) {
        formattedData.city = data.city;
      }
      if (data.state && data.state.trim()) {
        formattedData.state = data.state;
      }
      if (data.pincode && data.pincode.trim()) {
        formattedData.pincode = data.pincode;
      }
      
      await userService.updateUser(editingUser.id, formattedData);
      setOpenDialog(false);
      setEditingUser(null);
      reset();
      fetchUsers();
    } catch (error: any) {
      console.error('Error updating user:', error);
      console.error('Error response:', error.response?.data);
      
      // Extract validation errors
      let errorMessage = 'Failed to update user';
      
      if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        // Show all validation errors
        const errorMessages = error.response.data.errors.map((err: any) => 
          `${err.field}: ${err.message}`
        );
        errorMessage = `Validation errors:\n${errorMessages.join('\n')}`;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      setError(errorMessage);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await userService.deleteUser(userId);
        // Clear cache to ensure fresh data
        userService.clearCache();
        // Refresh users list - this will filter out inactive users by default
        await fetchUsers();
      } catch (error: any) {
        setError(error.response?.data?.message || 'Failed to delete user');
      }
    }
  };

  const handleRegeneratePIN = async (userId: string) => {
    try {
      const response = await userService.regeneratePIN(userId);
      setGeneratedPIN(response.pin);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to regenerate PIN');
    }
  };

  const handleReactivateUser = async (userId: string) => {
    if (window.confirm('Are you sure you want to reactivate this user?')) {
      try {
        await userService.updateUserStatus(userId, true);
        // Clear cache to ensure fresh data
        userService.clearCache();
        // Refresh users list
        await fetchUsers();
      } catch (error: any) {
        setError(error.response?.data?.message || 'Failed to reactivate user');
      }
    }
  };

  const handleViewUserProfile = (userId: string) => {
    navigate(`/users/${userId}`);
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedUsers(filteredUsers.map(user => user.id));
    } else {
      setSelectedUsers([]);
    }
  };

  const handleSelectUser = (userId: string) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const isSelected = (userId: string) => selectedUsers.includes(userId);

  const handleBulkDelete = async () => {
    if (window.confirm(`Are you sure you want to delete ${selectedUsers.length} users?`)) {
      try {
        // Stagger delete requests to prevent rate limiting
        for (let i = 0; i < selectedUsers.length; i++) {
          await userService.deleteUser(selectedUsers[i]);
          if (i < selectedUsers.length - 1) {
            await delay(300); // Small delay between deletions
          }
        }
        setSelectedUsers([]);
        // Clear cache to ensure fresh data
        userService.clearCache();
        // Refresh users list - this will filter out inactive users by default
        await fetchUsers();
      } catch (error: any) {
        setError(error.response?.data?.message || 'Failed to delete users');
      }
    }
    setBulkActionAnchor(null);
  };

  const handleBulkNotification = () => {
    // Implement bulk notification logic
    console.log('Send notification to:', selectedUsers);
    setBulkActionAnchor(null);
  };

  const handleBulkActionClick = (event: React.MouseEvent<HTMLElement>) => {
    setBulkActionAnchor(event.currentTarget);
  };

  const handleBulkActionClose = () => {
    setBulkActionAnchor(null);
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case USER_ROLES.super_admin:
        return 'error';
      case USER_ROLES.cluster_level_manager:
        return 'warning';
      case USER_ROLES.store_manager:
        return 'info';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Top Action Bar */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} gap={2}>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setOpenDialog(true)}
          sx={{
            background: 'linear-gradient(135deg, #081122 0%, #1a2a3a 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #1a2a3a 0%, #081122 100%)',
            },
            boxShadow: '0 4px 12px rgba(8, 17, 34, 0.3)',
            px: 3,
            py: 1.5,
          }}
        >
          Add User
        </Button>
        
        <Box display="flex" gap={2} alignItems="center" flex={1} justifyContent="flex-end">
          {/* Status Filter */}
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filters.is_active}
              label="Status"
              onChange={(e) => setFilters(prev => ({ ...prev, is_active: e.target.value }))}
            >
              <MenuItem value="true">Active Users</MenuItem>
              <MenuItem value="false">Deleted Users</MenuItem>
              <MenuItem value="all">All Users</MenuItem>
            </Select>
          </FormControl>
        
        <TextField
          size="small"
          placeholder="Search users..."
          value={filters.search}
          onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
          InputProps={{
            startAdornment: <Search sx={{ mr: 1, color: '#6c757d', fontSize: 18 }} />,
          }}
          sx={{ 
            minWidth: 300,
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              backgroundColor: '#f8f9fa',
              '&:hover': {
                backgroundColor: '#f0f0f0',
              },
              '&.Mui-focused': {
                backgroundColor: '#fff',
              },
            }
          }}
        />
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {generatedPIN && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setGeneratedPIN('')}>
          Generated PIN: {generatedPIN}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <FilterList sx={{ mr: 1, color: '#6c757d' }} />
          <Typography variant="h6" sx={{ color: '#081122', fontWeight: 600 }}>
            Filters
          </Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Store</InputLabel>
              <Select
                value={filters.store_id}
                label="Store"
                onChange={(e) => setFilters(prev => ({ ...prev, store_id: e.target.value }))}
              >
                <MenuItem value="all">All Stores</MenuItem>
                {stores.map((store) => (
                  <MenuItem key={store.id} value={store.id}>
                    {store.name} ({store.code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Cluster</InputLabel>
              <Select
                value={filters.cluster_id}
                label="Cluster"
                onChange={(e) => setFilters(prev => ({ ...prev, cluster_id: e.target.value }))}
              >
                <MenuItem value="all">All Clusters</MenuItem>
                {clusters.map((cluster) => (
                  <MenuItem key={cluster.id} value={cluster.id}>
                    {cluster.name} ({cluster.code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Vendor</InputLabel>
              <Select
                value={filters.vendor_id}
                label="Vendor"
                onChange={(e) => setFilters(prev => ({ ...prev, vendor_id: e.target.value }))}
              >
                <MenuItem value="all">All Vendors</MenuItem>
                <MenuItem value="none">
                  <em>No vendor / direct hire</em>
                </MenuItem>
                {vendors.map((vendor) => (
                  <MenuItem key={vendor.id} value={vendor.id}>
                    {vendor.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Role</InputLabel>
              <Select
                value={filters.role}
                label="Role"
                onChange={(e) => setFilters(prev => ({ ...prev, role: e.target.value }))}
              >
                <MenuItem value="all">All Roles</MenuItem>
                {USER_ROLE_OPTIONS.map((role) => (
                  <MenuItem key={role.value} value={role.value}>
                    {role.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Category Level</InputLabel>
              <Select
                value={filters.category_level}
                label="Category Level"
                onChange={(e) => setFilters(prev => ({ ...prev, category_level: e.target.value }))}
              >
                <MenuItem value="all">All Levels</MenuItem>
                {CATEGORY_LEVEL_OPTIONS.map((level) => (
                  <MenuItem key={level.value} value={level.value}>
                    {level.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Employee Type</InputLabel>
              <Select
                value={filters.employee_type}
                label="Employee Type"
                onChange={(e) => setFilters(prev => ({ ...prev, employee_type: e.target.value }))}
              >
                <MenuItem value="all">All Types</MenuItem>
                {EMPLOYEE_TYPE_OPTIONS.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={clearFilters}
              fullWidth
              size="small"
            >
              Clear Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Main Content Layout */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        {/* Main Content Area */}
        <Box sx={{ flex: 1 }}>

      {/* Bulk Actions */}
      {selectedUsers.length > 0 && (
        <Paper sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: '#f5f5f5' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" sx={{ color: '#666' }}>
              {selectedUsers.length} user(s) selected
            </Typography>
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<Delete />}
              onClick={handleBulkDelete}
            >
              Delete Selected
            </Button>
            <Button
              variant="outlined"
              color="primary"
              size="small"
              startIcon={<Notifications />}
              onClick={handleBulkNotification}
            >
              Send Notification
            </Button>
          </Box>
        </Paper>
      )}

      {/* Users Table */}
      <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
        <Table sx={{ minWidth: 1400 }}>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selectedUsers.length > 0 && selectedUsers.length < filteredUsers.length}
                  checked={filteredUsers.length > 0 && selectedUsers.length === filteredUsers.length}
                  onChange={(e) => {
                    e.stopPropagation();
                    handleSelectAll(e);
                  }}
                />
              </TableCell>
              <TableCell>Employee ID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Official Email</TableCell>
              <TableCell>PIN</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Job Title</TableCell>
              <TableCell>Employee Type</TableCell>
              <TableCell>Category Level</TableCell>
              <TableCell>Joining Date</TableCell>
              <TableCell>Date of Birth</TableCell>
              <TableCell>Gender</TableCell>
              <TableCell>Cluster</TableCell>
              <TableCell>Store</TableCell>
              <TableCell>Vendor</TableCell>
              <TableCell>Shift</TableCell>
              <TableCell>Manager</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.map((user) => (
              <TableRow 
                key={user.id}
                hover
                sx={{ 
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  }
                }}
                onClick={() => handleViewUserProfile(user.id)}
              >
                <TableCell 
                  padding="checkbox"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Checkbox
                    checked={isSelected(user.id)}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleSelectUser(user.id);
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 500, color: '#1976d2' }}>
                    {user.employee_id}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <ProfileAvatar
                      imageUrl={user.profile_image_url}
                      firstName={user.first_name}
                      lastName={user.last_name}
                      sx={{ width: 32, height: 32, mr: 1 }}
                    />
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {user.first_name} {user.last_name}
                      </Typography>
                      {user.address && (
                        <Typography variant="caption" sx={{ color: '#666', fontSize: '0.75rem' }}>
                          {user.address.length > 30 ? `${user.address.substring(0, 30)}...` : user.address}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>{user.phone}</TableCell>
                <TableCell>{user.email || 'N/A'}</TableCell>
                <TableCell>{user.official_email || 'N/A'}</TableCell>
                <TableCell>
                  {user.pin_plain ? (
                    <Chip
                      label={user.pin_plain}
                      color="primary"
                      size="small"
                      variant="outlined"
                    />
                  ) : (
                    <Chip
                      label="Hidden"
                      color="default"
                      size="small"
                      variant="outlined"
                    />
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    label={USER_ROLE_OPTIONS.find(r => r.value === user.role)?.label || user.role}
                    color={getRoleColor(user.role) as any}
                    size="small"
                  />
                </TableCell>
                <TableCell>{user.job_title || 'N/A'}</TableCell>
                <TableCell>
                  {user.employee_type && (
                    <Chip
                      label={user.employee_type.toUpperCase()}
                      size="small"
                      color="secondary"
                      variant="outlined"
                    />
                  )}
                </TableCell>
                <TableCell>
                  {user.category_level && (
                    <Chip
                      label={user.category_level.toUpperCase()}
                      size="small"
                      color="info"
                      variant="outlined"
                    />
                  )}
                </TableCell>
                <TableCell>
                  {user.joining_date ? new Date(user.joining_date).toLocaleDateString() : 'N/A'}
                </TableCell>
                <TableCell>
                  {user.date_of_birth ? new Date(user.date_of_birth).toLocaleDateString() : 'N/A'}
                </TableCell>
                <TableCell>
                  {user.gender && (
                    <Chip
                      label={user.gender.toUpperCase()}
                      size="small"
                      color="default"
                      variant="outlined"
                    />
                  )}
                </TableCell>
                <TableCell>{user.cluster?.name || 'N/A'}</TableCell>
                <TableCell>{user.store?.name || 'N/A'}</TableCell>
                <TableCell>{user.vendor?.name || '—'}</TableCell>
                <TableCell>
                  {(() => {
                    const shift = shifts.find(s => s.id === user.shift_id);
                    return shift ? shift.name : 'N/A';
                  })()}
                </TableCell>
                <TableCell>
                  {user.manager ? `${user.manager.first_name} ${user.manager.last_name}` : 'N/A'}
                </TableCell>
                <TableCell>
                  <Chip
                    label={user.is_active ? 'Active' : 'Inactive'}
                    color={user.is_active ? 'success' : 'error'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {user.is_active ? (
                      <>
                    {canEditUser && (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditUser(user);
                        }}
                        title="Edit User"
                        color="primary"
                      >
                        <Edit />
                      </IconButton>
                    )}
                    {canEditUser && (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRegeneratePIN(user.id);
                        }}
                        title="Regenerate PIN"
                      >
                        <Refresh />
                      </IconButton>
                    )}
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        openWorkSchedule(user);
                      }}
                      title="Work Timings"
                    >
                      <AccessTime />
                    </IconButton>
                    {canEditUser && (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteUser(user.id);
                        }}
                        title="Delete User"
                        color="error"
                      >
                        <Delete />
                      </IconButton>
                    )}
                      </>
                    ) : (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleReactivateUser(user.id);
                        }}
                        title="Reactivate User"
                        color="success"
                      >
                        <CheckCircle />
                      </IconButton>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
        </Box>
      </Box>

      {/* Create User Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingUser ? 'Edit User' : 'Create New User'}</DialogTitle>
        <form onSubmit={handleSubmit((data) => {
          console.log('Form submitted with data:', data);
          console.log('Form errors:', errors);
          if (editingUser) {
            handleUpdateUser(data);
          } else {
            handleCreateUser(data);
          }
        })}>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={6}>
                <TextField
                  {...register('first_name')}
                  label="First Name"
                  fullWidth
                  error={!!errors.first_name}
                  helperText={errors.first_name?.message}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  {...register('last_name')}
                  label="Last Name"
                  fullWidth
                  error={!!errors.last_name}
                  helperText={errors.last_name?.message}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  {...register('phone')}
                  label="Phone"
                  fullWidth
                  error={!!errors.phone}
                  helperText={errors.phone?.message}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  {...register('email')}
                  label="Email"
                  fullWidth
                  error={!!errors.email}
                  helperText={errors.email?.message}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  {...register('official_email')}
                  label="Official Email"
                  fullWidth
                  error={!!errors.official_email}
                  helperText={errors.official_email?.message}
                />
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth error={!!errors.role}>
                  <InputLabel>Role</InputLabel>
                  <Select
                    {...register('role')}
                    label="Role"
                    value={watch('role') || ''}
                    onChange={(e) => {
                      const newRole = e.target.value;
                      setValue('role', newRole);
                      // Clear cluster, store, shift when switching to super_admin
                      if (newRole === USER_ROLES.super_admin) {
                        setValue('cluster_id', '');
                        setValue('store_id', '');
                        setValue('shift_id', '');
                      }
                    }}
                  >
                    {USER_ROLE_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.role && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                      {errors.role.message}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              {!isSuperAdmin && (
                <>
              <Grid item xs={6}>
                <FormControl fullWidth error={!!errors.cluster_id}>
                  <InputLabel>Cluster</InputLabel>
                  <Select
                    {...register('cluster_id')}
                    label="Cluster"
                    value={watch('cluster_id') || ''}
                    onChange={(e) => setValue('cluster_id', e.target.value)}
                  >
                    {clusters.map((cluster) => (
                      <MenuItem key={cluster.id} value={cluster.id}>
                        {cluster.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.cluster_id && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                      {errors.cluster_id.message}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth error={!!errors.store_id}>
                  <InputLabel>Store</InputLabel>
                  <Select
                    {...register('store_id')}
                    label="Store"
                    value={watch('store_id') || ''}
                    onChange={(e) => setValue('store_id', e.target.value)}
                  >
                    {stores.map((store) => (
                      <MenuItem key={store.id} value={store.id}>
                        {store.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.store_id && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                      {errors.store_id.message}
                    </Typography>
                  )}
                  </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth error={!!errors.shift_id}>
                      <InputLabel>Shift</InputLabel>
                  <Select
                    {...register('shift_id')}
                        label="Shift"
                    value={watch('shift_id') || ''}
                    onChange={(e) => setValue('shift_id', e.target.value)}
                  >
                    {shifts.map((shift) => (
                      <MenuItem key={shift.id} value={shift.id}>
                        {shift.name} {shift.cluster ? `(${shift.cluster.name})` : '(General)'}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.shift_id && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                      {errors.shift_id.message}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
                </>
              )}
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Vendor (optional)</InputLabel>
                  <Select
                    {...register('vendor_id')}
                    label="Vendor (optional)"
                    value={watch('vendor_id') || ''}
                    onChange={(e) => setValue('vendor_id', e.target.value)}
                  >
                    <MenuItem value="">
                      <em>Hired directly / not applicable</em>
                    </MenuItem>
                    {vendors.map((vendor) => (
                      <MenuItem key={vendor.id} value={vendor.id}>
                        {vendor.name}
                        {vendor.code ? ` (${vendor.code})` : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  {...register('job_title')}
                  label="Job Title"
                  fullWidth
                  error={!!errors.job_title}
                  helperText={errors.job_title?.message}
                />
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth error={!!errors.employee_type}>
                  <InputLabel>Employee Type</InputLabel>
                  <Select
                    {...register('employee_type')}
                    label="Employee Type"
                    value={watch('employee_type') || ''}
                    onChange={(e) => setValue('employee_type', e.target.value as any)}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {EMPLOYEE_TYPE_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.employee_type && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                      {errors.employee_type.message}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth error={!!errors.category_level}>
                  <InputLabel>Category Level</InputLabel>
                  <Select
                    {...register('category_level')}
                    label="Category Level"
                    value={watch('category_level') || ''}
                    onChange={(e) => setValue('category_level', e.target.value)}
                  >
                    {CATEGORY_LEVEL_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.category_level && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                      {errors.category_level.message}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                {/* Write-once. The backend rejects any change to an existing
                    joining date, so the field is locked here to match rather
                    than letting the user type an edit that will be refused. */}
                <TextField
                  {...register('joining_date')}
                  label={
                    editingUser?.joining_date
                      ? 'Joining Date (locked)'
                      : 'Joining Date (Optional)'
                  }
                  type="date"
                  fullWidth
                  disabled={!!editingUser?.joining_date}
                  InputLabelProps={{ shrink: true }}
                  error={!!errors.joining_date}
                  helperText={
                    errors.joining_date?.message ||
                    (editingUser?.joining_date
                      ? 'Joining date cannot be changed once set'
                      : undefined)
                  }
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  {...register('date_of_birth')}
                  label="Date of Birth (Optional)"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  error={!!errors.date_of_birth}
                  helperText={errors.date_of_birth?.message}
                />
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth error={!!errors.gender}>
                  <InputLabel>Gender</InputLabel>
                  <Select
                    {...register('gender')}
                    label="Gender"
                    value={watch('gender') || ''}
                    onChange={(e) => setValue('gender', e.target.value as any)}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {GENDER_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.gender && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                      {errors.gender.message}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  {...register('address')}
                  label="Address"
                  fullWidth
                  multiline
                  rows={2}
                  error={!!errors.address}
                  helperText={errors.address?.message}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
            <Button type="submit" variant="contained">Update User</Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Work Timings Dialog */}
      <Dialog
        open={openScheduleDialog}
        onClose={() => setOpenScheduleDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Work Timings {editingUser ? `- ${editingUser.first_name} ${editingUser.last_name}` : ''}
        </DialogTitle>
        <DialogContent>
          {scheduleLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ mt: 1 }}>
              <Grid container sx={{ mb: 1, fontWeight: 600, color: '#555' }}>
                <Grid item xs={4}>
                  Day
                </Grid>
                <Grid item xs={3}>
                  Week Off
                </Grid>
                <Grid item xs={5}>
                  Shift
                </Grid>
              </Grid>
              {userSchedule.map((entry, index) => {
                const dayMeta = DAY_LABELS.find((d) => d.value === entry.day_of_week)!;
                return (
                  <Grid
                    container
                    key={entry.day_of_week}
                    alignItems="center"
                    sx={{ mb: 1 }}
                  >
                    <Grid item xs={4}>
                      <Typography sx={{ fontWeight: 500 }}>{dayMeta.label}</Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Checkbox
                        checked={entry.is_week_off}
                        onChange={(e) =>
                          updateScheduleEntry(index, {
                            is_week_off: e.target.checked,
                            shift_id: e.target.checked ? null : entry.shift_id,
                          })
                        }
                      />
                    </Grid>
                    <Grid item xs={5}>
                      <FormControl fullWidth size="small" disabled={entry.is_week_off}>
                        <Select
                          value={entry.shift_id || ''}
                          displayEmpty
                          onChange={(e) =>
                            updateScheduleEntry(index, {
                              shift_id: (e.target.value as string) || null,
                            })
                          }
                          renderValue={(selected) => {
                            if (!selected) return 'Select Shift';
                            const shift = shifts.find((s) => s.id === selected);
                            return shift ? shift.name : 'Select Shift';
                          }}
                        >
                          <MenuItem value="">
                            <em>Select Shift</em>
                          </MenuItem>
                          {shifts.map((shift) => (
                            <MenuItem key={shift.id} value={shift.id}>
                              {shift.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenScheduleDialog(false)}>Cancel</Button>
          <Button
            onClick={handleSaveWorkSchedule}
            variant="contained"
            disabled={scheduleLoading}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UsersPage;